import IconService from "icon-sdk-js";
import axios from "axios";
import {
  IconAmount,
  IconConverter,
  HttpProvider,
  IconWallet,
  IconBuilder,
  SignedTransaction,
  IconUtil,
} from "icon-sdk-js";
import { BigNumber } from "bignumber.js";
import logger from "../utils/logger.ts";

// Import the local package using dynamic import
let AppIcx;
const loadAppIcx = async () => {
  if (!AppIcx) {
    const module = await import("../packages/hw-app-icx/lib/Icx.js");
    AppIcx = module.default;
  }
  return AppIcx;
};

const { CallBuilder } = IconService.IconBuilder;
const IconValidator = IconService.IconValidator;

const GANGSTA_SCORE = import.meta.env.VITE_GANGSTA_SCORE;
const GBET_SCORE = import.meta.env.VITE_GBET_SCORE;
const SKILL_SCORE = import.meta.env.VITE_SKILL_SCORE;
const GG_SCORE = import.meta.env.VITE_GG_TOKEN_SCORE;
const STORY_SCORE = import.meta.env.VITE_STORY_SCORE;

const MARKETPLACE_SCORE = import.meta.env.VITE_MARKETPLACE_SCORE;
const GOLDEN_KEY_SCORE = import.meta.env.VITE_GOLDEN_KEY_SCORE;
const VESTING_SCORE = import.meta.env.VITE_CROWN_VESTING_SCORE;
const WALLET_NETWORK = import.meta.env.VITE_WALLET_URL;
const NETWORK_ID = import.meta.env.VITE_NETWORK_ID;
const ILLUSTRATION_SCORE = import.meta.env.VITE_ILLUSTRATION_SCORE;
const STEP_LIMIT_API = import.meta.env.VITE_DYNAMIC_STEPLIMIT_API;

const provider = new HttpProvider(WALLET_NETWORK);

const callBuilder = new CallBuilder();
const iconService = new IconService(provider);

const WALLET_TYPE = {
  LEDGER: "ledger",
  ICONEX: "iconex",
};

const signTransaction = async (transaction, walletPath, transport) => {
  const rawTransaction = IconConverter.toRawTransaction(transaction);
  const hashKey = IconUtil.generateHashKey(rawTransaction);
  const AppIcxClass = await loadAppIcx();
  const icx = new AppIcxClass(transport);
  const { signedRawTxBase64 } = await icx.signTransaction(walletPath, hashKey);

  rawTransaction.signature = signedRawTxBase64;

  return {
    getProperties: () => rawTransaction,
    getSignature: () => signedRawTxBase64,
  };
};

export const isValidAddress = (address) => {
  try {
    const validAddress = IconValidator.isEoaAddress(address);
    return validAddress;
  } catch (e) {
    return false;
  }
};

export const getNftsold = async () => {
  try {
    const call = callBuilder
      .to(GANGSTA_SCORE)
      .method("getNftCount")
      .params({})
      .build();
    const totalnft = await iconService.call(call).execute();

    return parseInt(totalnft);
  } catch (e) {
    return 0;
  }
};

export const getCharacterInfo = (nft_id) => {
  return new Promise(async (resolve, reject) => {
    try {
      const call = callBuilder
        .to(GANGSTA_SCORE)
        .method("getCharacterInfo")
        .params({
          nftId: `0x${parseInt(nft_id).toString(16)}`,
        })
        .build();
      const nft = await iconService.call(call).execute();

      resolve(nft);
    } catch (e) {
      if (typeof e === "string") {
        if (e.includes("Generating NFT")) {
          resolve({
            msg: "Generating NFT",
            meta_id: e.split(".")[1].replace("(", "").replace(")", ""),
          });
        }
      }
      resolve({
        msg: "Invalid",
      });
    }
  });
};

export const getGbetinWallet = async (address) => {
  try {
    const call = callBuilder
      .to(GANGSTA_SCORE)
      .method("getGbetBalance")
      .params({
        address: address,
      })
      .build();
    const balance = await iconService.call(call).execute();
    const parsed = parseInt(balance) / Math.pow(10, 18);

    return parsed;
  } catch (e) {
    return 0;
  }
};

export const getNftCreationCost = async (count) => {
  let convertedCount = "0x0";

  if (count) {
    convertedCount = `0x${count.toString(16)}`;
  }

  const call = callBuilder
    .to(GANGSTA_SCORE)
    .method("getCreationCost")
    .params({ nftCount: convertedCount })
    .build();
  const cost = await iconService.call(call).execute();
  const parsedCost = parseInt(cost);
  const fixedCost = parsedCost / Math.pow(10, 18);

  return fixedCost;
};

export const getNftNameChangeCost = async () => {
  try {
    const call = callBuilder
      .to(SKILL_SCORE)
      .method("getNameChangeCost")
      .params({})
      .build();
    const cost = await iconService.call(call).execute();
    const parsedCost = parseInt(cost);
    return parsedCost / Math.pow(10, 18);
  } catch (e) {
    logger.serviceError('icon', 'getNftNameChangeCost', e);
  }
};

export const changeSalesPrice = async (
  payload,
  walletType,
  walletPath = null,
  transport = null
) => {
  try {
    const sellDetails = await getSaleByNftId(payload.nft_id);
    const saleId = sellDetails.id;
    let roundedPrice = (payload.price * Math.pow(10, 18))
      .toFixedSpecial(0)
      .toString();
    if (roundedPrice.charAt(roundedPrice.length - 1) === ".") {
      roundedPrice = roundedPrice.slice(0, -1);
    }
    const params = {
      rate: roundedPrice,
      saleId: IconConverter.toHex(saleId),
    };
    const stepLimit = await getStepLimit(
      payload.address,
      "changeRate",
      MARKETPLACE_SCORE,
      params,
      0
    );
    const callTransactionBuilder = new IconBuilder.CallTransactionBuilder();

    const txObj = callTransactionBuilder
      .from(payload.address)
      .to(MARKETPLACE_SCORE)
      .value(IconAmount.of(0, IconAmount.Unit.ICX).toLoop())
      .stepLimit(stepLimit)
      .nid(IconConverter.toBigNumber(NETWORK_ID))
      .nonce(IconConverter.toBigNumber("1"))
      .version(IconConverter.toBigNumber("3"))
      .timestamp(new Date().getTime() * 1000)
      .method("changeRate")
      .params(params)
      .build();

    if (walletType == WALLET_TYPE.LEDGER) {
      const signedTransaction = await signTransaction(
        txObj,
        walletPath,
        transport
      );

      const res = await iconService
        .sendTransaction(signedTransaction)
        .execute();

      return res;
    } else {
      const scoreData = {};

      scoreData.value = JSON.stringify({
        jsonrpc: "2.0",
        method: "icx_sendTransaction",
        params: IconConverter.toRawTransaction(txObj),
        id: 50889,
      });

      const parsed = JSON.parse(scoreData.value);

      window.dispatchEvent(
        new CustomEvent("ICONEX_RELAY_REQUEST", {
          detail: {
            type: "REQUEST_JSON-RPC",
            payload: parsed,
          },
        })
      );
    }
  } catch (e) {
    alert(e);
    logger.serviceError('icon', 'changeSalesPrice', e, { payload, walletType });
    return -1;
  }
};

// export const getNftNameChangeCost = async (nft_id) => {
//   const call = callBuilder
//     .to(SKILL_SCORE)
//     .method("get_name_change_cost")
//     .params({ nft_id: `0x${parseInt(nft_id).toString(16)}` })
//     .build();
//   const cost = await iconService.call(call).execute();

//   const parsedCost = parseInt(cost);

//   return parsedCost / Math.pow(10, 18);
// };

export const createNft = async (
  address,
  nftCount,
  totalcost,
  walletType,
  walletPath = null,
  transport = null
) => {
  try {
    const callTransactionBuilder = new IconBuilder.CallTransactionBuilder();
    const params = {
      nftCount: nftCount,
    };
    // const stepLimit = await getStepLimit(
    //   address,
    //   "requestCharacterCreation",
    //   GANGSTA_SCORE,
    //   params,
    //   totalcost
    // );

    const callTransactionData = callTransactionBuilder
      .from(address)
      .to(GANGSTA_SCORE)
      .nid(IconConverter.toBigNumber(NETWORK_ID))
      .value(IconAmount.of(totalcost, IconAmount.Unit.ICX).toLoop())
      .timestamp(new Date().getTime() * 1000)
      .stepLimit(40000000)
      .version(IconConverter.toBigNumber(3))
      .method("requestCharacterCreation")
      .params(params)
      .build();

    if (WALLET_TYPE.LEDGER == walletType) {
      const signedTransaction = await signTransaction(
        callTransactionData,
        walletPath,
        transport
      );
      const res = await iconService
        .sendTransaction(signedTransaction)
        .execute();

      return res;
    } else {
      const scoreData = {};

      scoreData.value = JSON.stringify({
        jsonrpc: "2.0",
        method: "icx_sendTransaction",
        params: IconConverter.toRawTransaction(callTransactionData),
        id: 50889,
      });

      const parsed = JSON.parse(scoreData.value);

      window.dispatchEvent(
        new CustomEvent("ICONEX_RELAY_REQUEST", {
          detail: {
            type: "REQUEST_JSON-RPC",
            payload: parsed,
          },
        })
      );
    }
  } catch (e) {
    // alert(e);
    logger.serviceError('icon', 'createNft', e, { address, nftCount, totalcost, walletType });
    return -1;
  }
};

export const handleLogin = () => {
  window.dispatchEvent(
    new CustomEvent("ICONEX_RELAY_REQUEST", {
      detail: {
        type: "REQUEST_ADDRESS",
      },
    })
  );
};

export const checkIfHasAccount = () => {
  window.dispatchEvent(
    new CustomEvent("ICONEX_RELAY_REQUEST", {
      detail: {
        type: "REQUEST_ADDRESS",
        payload: {
          jsonrpc: "2.0",
          id: 1234,
        },
      },
    })
  );
};

export const changeName = async (
  address,
  name,
  nft_id,
  walletType,
  walletPath = null,
  transport = null
) => {
  try {
    const callTransactionBuilder = new IconBuilder.CallTransactionBuilder();
    const params = {
      nftId: `0x${parseInt(nft_id).toString(16)}`,
      nftName: name,
    };
    const stepLimit = await getStepLimit(
      address,
      "changeName",
      SKILL_SCORE,
      params,
      0
    );
    const txObj = callTransactionBuilder
      .from(address)
      .to(SKILL_SCORE)
      .value(IconAmount.of(0, IconAmount.Unit.ICX).toLoop())
      .stepLimit(stepLimit)
      .nid(IconConverter.toBigNumber(NETWORK_ID))
      .nonce(IconConverter.toBigNumber("1"))
      .version(IconConverter.toBigNumber("3"))
      .timestamp(new Date().getTime() * 1000)
      .method("changeName")
      .params(params)
      .build();

    if (walletType == WALLET_TYPE.LEDGER) {
      const signedTransaction = await signTransaction(
        txObj,
        walletPath,
        transport
      );

      const res = await iconService
        .sendTransaction(signedTransaction)
        .execute();

      return res;
    } else {
      const scoreData = {};

      scoreData.value = JSON.stringify({
        jsonrpc: "2.0",
        method: "icx_sendTransaction",
        params: IconConverter.toRawTransaction(txObj),
        id: 50889,
      });

      const parsed = JSON.parse(scoreData.value);

      window.dispatchEvent(
        new CustomEvent("ICONEX_RELAY_REQUEST", {
          detail: {
            type: "REQUEST_JSON-RPC",
            payload: parsed,
          },
        })
      );
    }
  } catch (e) {
    // console.log(e);
    return -1;
  }
};

export const getSkillChangeCost = async (nft_id, amount) => {
  const call = callBuilder
    .to(SKILL_SCORE)
    .method("getSkillLevelCost")
    .params({
      nftId: `0x${parseInt(nft_id).toString(16)}`,
      incAmt: amount.toString(),
    })
    .build();
  const res = await iconService.call(call).execute();
  let skillChangeCostRes = {
    breakdown: [],
    totalCost: 0,
  };

  res.breakdown.forEach((c) => {
    skillChangeCostRes.breakdown.push([
      parseInt(c[0]),
      parseInt(c[1]) / Math.pow(10, 18),
    ]);
  });

  skillChangeCostRes.totalCost = parseInt(res.totalCost) / Math.pow(10, 18);

  return skillChangeCostRes;
};

export const getTransactionResult = async (tx) => {
  try {
    const transactionResult = await iconService
      .getTransactionResult(tx)
      .execute();

    return {
      status: transactionResult.status === 1 ? "success" : "error",
      msg:
        transactionResult.status === 1
          ? "Success"
          : transactionResult.failure.message,
      tx: tx,
    };
  } catch (e) {
    logger.transactionError(tx, e);
    if (
      e.includes("Pending") ||
      e.includes("Executing") ||
      e.includes("not found") ||
      e === "[RPC ERROR] Invalid params txHash"
    ) {
      return {
        status: "pending",
        msg: "Pending Transaction",
        tx: tx,
      };
    }

    return {
      status: "error",
      msg: "Invalid Transaction",
      tx: tx,
    };
  }
};

export const printApis = async () => {
  const apiList1 = await iconService.getScoreApi(GANGSTA_SCORE).execute();
  const apiList2 = await iconService.getScoreApi(GBET_SCORE).execute();
  const apiList3 = await iconService.getScoreApi(SKILL_SCORE).execute();
  const apiList4 = await iconService.getScoreApi(MARKETPLACE_SCORE).execute();

  // console.log("GANGSTA_SCORE", apiList1.getList());
  // console.log("GBET_SCORE", apiList2.getList());
  // console.log("SKILL_SCORE", apiList3.getList());
  // console.log("MARKETPLACE_SCORE", apiList4.getList());
};

// export const getOwnedNft = async (address) => {
//   try {
//     const call = callBuilder
//       .to(GANGSTA_SCORE)
//       .method("get_all_nft_owned")
//       .params({ address: address, page: "0x2" })
//       .build();
//     const nfts = await iconService.call(call).execute();

//     return nfts;
//   } catch (error) {
//     console.log("error", error);
//     return "Invalid Address";
//   }
// };

export const _getOwnedNft = async (address, page) => {
  try {
    const call = callBuilder
      .to(GANGSTA_SCORE)
      .method("getAllNftOwned")
      .params({ address: address, page: page ? page : "0x1" })
      .build();
    const nfts = await iconService.call(call).execute();

    return nfts;
  } catch (error) {
    return "Invalid Address";
  }
};

export const verifyUniqueName = async (name) => {
  try {
    const call = callBuilder
      .to(SKILL_SCORE)
      .method("verifyNameExistence")
      .params({ nftName: name })
      .build();
    const checkName = await iconService.call(call).execute();

    return parseInt(checkName);
  } catch (error) {
    return -1;
  }
};

export const getSalePrice = async (nft_id) => {
  try {
    const call = callBuilder
      .to(MARKETPLACE_SCORE)
      .method("getPrice")
      .params({ nftId: `0x${parseInt(nft_id).toString(16)}` })
      .build();
    const price = await iconService.call(call).execute();

    const parseValue = new BigNumber(price, 16);
    logger.warn('Sale price calculation', { nft_id, price: parseValue.dividedBy(BigNumber(10).pow(18)).toString() });

    return parseValue.dividedBy(BigNumber(10).pow(18))?.toNumber();
  } catch (e) {
    // console.log(e);
    return -1;
  }
};

export const checkIfMarketPlaceIsApproved = async (address) => {
  try {
    const call = callBuilder
      .to(GANGSTA_SCORE)
      .method("isApprovedForAll")
      .params({ _owner: address, _operator: MARKETPLACE_SCORE })
      .build();

    const res = await iconService.call(call).execute();
    return res;
  } catch (e) {
    // console.log(e);

    return -1;
  }
};

// export const setPrice = async (payload) => {
//   const call = callBuilder
//     .to(MARKETPLACE_SCORE)
//     .method("set_price")
//     .params({ nft_if: payload.nft_id, price: 20 })
//     .build();
//   const nfts = await iconService.call(call).execute();

//   return nfts;
// };

const getStepLimit = async (from, method, score, param, value) => {
  try {
    logger.debug("Step limit calculation", { NETWORK_ID, from, method, score, param, value });
    const info = {
      id: 1234,
      jsonrpc: "2.0",
      method: "debug_estimateStep",
      params: {
        from,
        data: {
          method,
          params: param || null,
        },
        dataType: "call",
        nid: `0x${parseFloat(NETWORK_ID).toString(16)}`,
        nonce: "0x1",
        timestamp: `0x${(new Date().getTime() * 1000).toString(16)}`,
        to: score,
        value: value ? `0x${(value * 10 ** 18).toString(16)}` : null,
        version: "0x3",
      },
    };
    const stepLimit = await axios.post(STEP_LIMIT_API, info);
    return `0x${(Number(stepLimit.data.result) + 1_000_000).toString(16)}`;
  } catch (error) {
    logger.serviceError('icon', 'getStepLimit', error, { from, method, score, param, value });
    return `0x${Number(90_000_000).toString(16)}`;
  }
};

export const approveMarketPlace = async (
  address,
  walletType,
  walletPath = null,
  transport = null
) => {
  try {
    const callTransactionBuilder = new IconBuilder.CallTransactionBuilder();
    const params = {
      _operator: MARKETPLACE_SCORE,
      _approved: "0x1",
    };
    const stepLimit = await getStepLimit(
      address,
      "setApprovalForAll",
      GANGSTA_SCORE,
      params,
      0
    );
    const txObj = callTransactionBuilder
      .from(address)
      .to(GANGSTA_SCORE)
      .value(IconAmount.of(0, IconAmount.Unit.ICX).toLoop())
      .stepLimit(stepLimit)
      .nid(IconConverter.toBigNumber(NETWORK_ID))
      .nonce(IconConverter.toBigNumber("1"))
      .version(IconConverter.toBigNumber("3"))
      .timestamp(new Date().getTime() * 1000)
      .method("setApprovalForAll")
      .params(params)
      .build();
    if (walletType == WALLET_TYPE.LEDGER) {
      const signedTransaction = await signTransaction(
        txObj,
        walletPath,
        transport
      );
      const res = await iconService
        .sendTransaction(signedTransaction)
        .execute();

      return res;
    } else {
      const scoreData = {};

      scoreData.value = JSON.stringify({
        jsonrpc: "2.0",
        method: "icx_sendTransaction",
        params: IconConverter.toRawTransaction(txObj),
        id: 50889,
      });

      const parsed = JSON.parse(scoreData.value);

      window.dispatchEvent(
        new CustomEvent("ICONEX_RELAY_REQUEST", {
          detail: {
            type: "REQUEST_JSON-RPC",
            payload: parsed,
          },
        })
      );
    }
  } catch (e) {
    // console.log(e);
    return -1;
  }
};

// const returnSellingPrice = (sellDetails) => {
//   return new Promise(async (resolve, reject) => {
//     console.log("sellDetails", sellDetails);
//     try {
//       if (sellDetails.length > 0) {
//         const price = sellDetails[0].price;
//         resolve(parseInt(price) / Math.pow(10, 18));
//       } else {
//         resolve(0);
//       }
//     } catch (e) {
//       console.log(e);
//       reject(-1);
//     }
//   });
// };

export const getSaleByNftId = (nftId) => {
  return new Promise(async (resolve, reject) => {
    try {
      const call = callBuilder
        .to(MARKETPLACE_SCORE)
        .method("getSaleByNftId")
        .params({
          score: GANGSTA_SCORE,
          nftId: `0x${parseInt(nftId).toString(16)}`,
        })
        .build();
      let sellDetails = await iconService.call(call).execute();
      if (typeof sellDetails === "undefined") {
        sellDetails = {};
      }
      resolve(sellDetails);
    } catch (e) {
      logger.serviceError('icon', 'getSaleByNftId', e, { nftId });
      resolve({});
    }
  });
};

export const buyListedNft = async (
  payload,
  walletType,
  walletPath = null,
  transport = null
) => {
  try {
    const sellDetails = await getSaleByNftId(payload.nft_id);
    const saleId = sellDetails.id;
    const params = {
      saleId: IconConverter.toHex(saleId),
    };
    const stepLimit = await getStepLimit(
      payload.address,
      "buy",
      MARKETPLACE_SCORE,
      params,
      payload.price
    );
    const callTransactionBuilder = new IconBuilder.CallTransactionBuilder();
    const txObj = callTransactionBuilder
      .from(payload.address)
      .to(MARKETPLACE_SCORE)
      .value(IconAmount.of(payload.price, IconAmount.Unit.ICX).toLoop())
      .stepLimit(stepLimit)
      .nid(IconConverter.toBigNumber(NETWORK_ID))
      .nonce(IconConverter.toBigNumber("1"))
      .version(IconConverter.toBigNumber("3"))
      .timestamp(new Date().getTime() * 1000)
      .method("buy")
      .params(params)
      .build();

    if (walletType == WALLET_TYPE.LEDGER) {
      const signedTransaction = await signTransaction(
        txObj,
        walletPath,
        transport
      );
      const res = await iconService
        .sendTransaction(signedTransaction)
        .execute();

      return res;
    } else {
      const scoreData = {};

      scoreData.value = JSON.stringify({
        jsonrpc: "2.0",
        method: "icx_sendTransaction",
        params: IconConverter.toRawTransaction(txObj),
        id: 50889,
      });

      const parsed = JSON.parse(scoreData.value);

      window.dispatchEvent(
        new CustomEvent("ICONEX_RELAY_REQUEST", {
          detail: {
            type: "REQUEST_JSON-RPC",
            payload: parsed,
          },
        })
      );
    }
  } catch (e) {
    // console.log(e);
    return -1;
  }
};

Number.prototype.toFixedSpecial = function (n) {
  var str = this.toFixed(n);
  if (str.indexOf("e+") < 0) return str;

  // if number is in scientific notation, pick (b)ase and (p)ower
  return (
    str
      .replace(".", "")
      .split("e+")
      .reduce(function (p, b) {
        return p + Array(b - p.length + 2).join(0);
      }) +
    "." +
    Array(n + 1).join(0)
  );
};

export const setSellingPrice = async (
  payload,
  walletType,
  walletPath = null,
  transport = null
) => {
  try {
    let roundedPrice = (payload.price * Math.pow(10, 18))
      .toFixedSpecial(0)
      .toString();
    if (roundedPrice.charAt(roundedPrice.length - 1) === ".") {
      roundedPrice = roundedPrice.slice(0, -1);
    }
    const callTransactionBuilder = new IconBuilder.CallTransactionBuilder();
    const params = {
      score: GANGSTA_SCORE,
      rate: roundedPrice,
      nftId: `0x${parseInt(payload.nft_id).toString(16)}`,
      // price: (payload.price * Math.pow(10, 18)).toString(),
      // price: IconAmount.of(payload.price, IconAmount.Unit.ICX).toLoop(),
    };
    const stepLimit = await getStepLimit(
      payload.address,
      "setPrice",
      MARKETPLACE_SCORE,
      params,
      0
    );
    const txObj = callTransactionBuilder
      .from(payload.address)
      .to(MARKETPLACE_SCORE)
      .value(IconAmount.of(0, IconAmount.Unit.ICX).toLoop())
      .stepLimit(stepLimit)
      .nid(IconConverter.toBigNumber(NETWORK_ID))
      .nonce(IconConverter.toBigNumber("1"))
      .version(IconConverter.toBigNumber("3"))
      .timestamp(new Date().getTime() * 1000)
      .method("setPrice")
      .params(params)
      .build();

    if (walletType == WALLET_TYPE.LEDGER) {
      const signedTransaction = await signTransaction(
        txObj,
        walletPath,
        transport
      );

      const res = await iconService
        .sendTransaction(signedTransaction)
        .execute();

      return res;
    } else {
      const scoreData = {};

      scoreData.value = JSON.stringify({
        jsonrpc: "2.0",
        method: "icx_sendTransaction",
        params: IconConverter.toRawTransaction(txObj),
        id: 50889,
      });

      const parsed = JSON.parse(scoreData.value);

      window.dispatchEvent(
        new CustomEvent("ICONEX_RELAY_REQUEST", {
          detail: {
            type: "REQUEST_JSON-RPC",
            payload: parsed,
          },
        })
      );
    }
  } catch (e) {
    logger.serviceError('icon', 'setSellingPrice', e, { payload, walletType });
    return -1;
  }
};

export const getGbetBalance = async (nft_id) => {
  const call = callBuilder
    .to(SKILL_SCORE)
    .method("getAllocatedClaimAmt")
    .params({ nftId: `0x${parseInt(nft_id).toString(16)}` })
    .build();
  const gbet = await iconService.call(call).execute();

  const parsedGbet = parseInt(gbet.total);
  const fixedCost = parsedGbet / Math.pow(10, 18);

  return fixedCost;
};

export const getGbetBalanceInWallet = async (address) => {
  const call = callBuilder
    .to(GBET_SCORE)
    .method("balanceOf")
    .params({ _owner: address })
    .build();
  const gbet = await iconService.call(call).execute();

  const parsedGbet = parseInt(gbet);
  const fixedCost = parsedGbet / Math.pow(10, 18);

  return fixedCost;
};

export const claimGbet = async (
  address,
  walletType,
  walletPath = null,
  transport = null
) => {
  try {
    const callTransactionBuilder = new IconBuilder.CallTransactionBuilder();
    const stepLimit = await getStepLimit(
      address,
      "claimAllocatedAmt",
      SKILL_SCORE,
      {},
      0
    );
    const txObj = callTransactionBuilder
      .from(address)
      .to(SKILL_SCORE)
      .value(IconAmount.of(0, IconAmount.Unit.ICX).toLoop())
      .stepLimit(stepLimit)
      .nid(IconConverter.toBigNumber(NETWORK_ID))
      .nonce(IconConverter.toBigNumber("1"))
      .version(IconConverter.toBigNumber("3"))
      .timestamp(new Date().getTime() * 1000)
      .method("claimAllocatedAmt")
      .params({})
      .build();

    if (walletType == WALLET_TYPE.LEDGER) {
      const signedTransaction = await signTransaction(
        txObj,
        walletPath,
        transport
      );
      const res = await iconService
        .sendTransaction(signedTransaction)
        .execute();

      return res;
    } else {
      const scoreData = {};

      scoreData.value = JSON.stringify({
        jsonrpc: "2.0",
        method: "icx_sendTransaction",
        params: IconConverter.toRawTransaction(txObj),
        id: 50889,
      });

      const parsed = JSON.parse(scoreData.value);

      window.dispatchEvent(
        new CustomEvent("ICONEX_RELAY_REQUEST", {
          detail: {
            type: "REQUEST_JSON-RPC",
            payload: parsed,
          },
        })
      );
    }
  } catch (e) {
    // console.log(e);
    return -1;
  }
};

export const claimIndividualGbet = async (
  address,
  nft_id,
  walletType,
  walletPath = null,
  transport = null
) => {
  try {
    const callTransactionBuilder = new IconBuilder.CallTransactionBuilder();
    const params = {
      nftId: `0x${parseInt(nft_id).toString(16)}`,
    };
    const stepLimit = await getStepLimit(
      address,
      "claimAllocatedAmt",
      SKILL_SCORE,
      params,
      0
    );
    const txObj = callTransactionBuilder
      .from(address)
      .to(SKILL_SCORE)
      .value(IconAmount.of(0, IconAmount.Unit.ICX).toLoop())
      .stepLimit(stepLimit)
      .nid(IconConverter.toBigNumber(NETWORK_ID))
      .nonce(IconConverter.toBigNumber("1"))
      .version(IconConverter.toBigNumber("3"))
      .timestamp(new Date().getTime() * 1000)
      .method("claimAllocatedAmt")
      .params(params)
      .build();

    if (walletType == WALLET_TYPE.LEDGER) {
      const signedTransaction = await signTransaction(
        txObj,
        walletPath,
        transport
      );
      const res = await iconService
        .sendTransaction(signedTransaction)
        .execute();

      return res;
    } else {
      const scoreData = {};

      scoreData.value = JSON.stringify({
        jsonrpc: "2.0",
        method: "icx_sendTransaction",
        params: IconConverter.toRawTransaction(txObj),
        id: 50889,
      });

      const parsed = JSON.parse(scoreData.value);

      window.dispatchEvent(
        new CustomEvent("ICONEX_RELAY_REQUEST", {
          detail: {
            type: "REQUEST_JSON-RPC",
            payload: parsed,
          },
        })
      );
    }
  } catch (e) {
    // console.log(e);
    return -1;
  }
};

export const getUserReservationInfo = (address) => {
  return new Promise(async (resolve, reject) => {
    try {
      const call = callBuilder
        .to(GANGSTA_SCORE)
        .method("getUserReservation")
        .params({ address })
        .build();
      const txDetails = await iconService.call(call).execute();

      resolve(txDetails);
    } catch (e) {
      reject(e);
    }
  });
};

export const getReservationInfo = () => {
  return new Promise(async (resolve, reject) => {
    try {
      const call = callBuilder
        .to(GANGSTA_SCORE)
        .method("getReservationInfo")
        .params({})
        .build();
      const txDetails = await iconService.call(call).execute();

      resolve(txDetails);
    } catch (e) {
      reject(e);
    }
  });
};

export const getPresaleDate = () => {
  return new Promise(async (resolve, reject) => {
    try {
      const call = callBuilder
        .to(SKILL_SCORE)
        .method("getDates")
        .params({})
        .build();
      const txDetails = await iconService.call(call).execute();

      resolve(txDetails);
    } catch (e) {
      reject(e);
    }
  });
};

const objectToHexWithPrefix = function (params) {
  const enc = new TextEncoder();
  const arrayBuffer = enc.encode(JSON.stringify(params));

  if (
    typeof arrayBuffer !== "object" ||
    arrayBuffer === null ||
    typeof arrayBuffer.byteLength !== "number"
  ) {
    throw new TypeError("Expected input to be an ArrayBuffer");
  }

  let view = new Uint8Array(arrayBuffer);
  let result = "";
  let value;

  for (let i = 0; i < view.length; i++) {
    value = view[i].toString(16);
    result += value.length === 1 ? "0" + value : value;
  }

  return `0x${result}`;
};

export const increaseStatusLevel = async (
  address,
  nft_id,
  skill,
  walletType,
  walletPath = null,
  transport = null
) => {
  try {
    const callTransactionBuilder = new IconBuilder.CallTransactionBuilder();

    for (let i = 1; i <= 5; i++) {
      if (!Object.keys(skill).includes(JSON.stringify(i))) {
        skill[i] = 0;
      }
    }

    const params = {
      nftId: `0x${Number(nft_id).toString(16)}`,
      skillIncMapping: objectToHexWithPrefix(skill),
    };
    const stepLimit = await getStepLimit(
      address,
      "increaseStatusLevel",
      SKILL_SCORE,
      params,
      0
    );
    const txObj = callTransactionBuilder
      .from(address)
      .to(SKILL_SCORE)
      .value(IconAmount.of(0, IconAmount.Unit.ICX).toLoop())
      .stepLimit(stepLimit)
      .nid(IconConverter.toBigNumber(NETWORK_ID))
      .nonce(IconConverter.toBigNumber("1"))
      .version(IconConverter.toBigNumber("3"))
      .timestamp(new Date().getTime() * 1000)
      .method("increaseStatusLevel")
      .params(params)
      .build();

    if (walletType == WALLET_TYPE.LEDGER) {
      const signedTransaction = await signTransaction(
        txObj,
        walletPath,
        transport
      );
      const res = await iconService
        .sendTransaction(signedTransaction)
        .execute();

      return res;
    } else {
      const scoreData = {};

      scoreData.value = JSON.stringify({
        jsonrpc: "2.0",
        method: "icx_sendTransaction",
        params: IconConverter.toRawTransaction(txObj),
        id: 50889,
      });

      const parsed = JSON.parse(scoreData.value);

      window.dispatchEvent(
        new CustomEvent("ICONEX_RELAY_REQUEST", {
          detail: {
            type: "REQUEST_JSON-RPC",
            payload: parsed,
          },
        })
      );
    }
  } catch (e) {
    // console.log("e", e);
    return -1;
  }
};

// const txBuilder = new IconService.IconBuilder.CallTransactionBuilder();
// const txData = txBuilder
//   .from(from)
//   .to(scoreAddr)
//   .nid(NID)
//   .timestamp(new Date().getTime() * 1000)
//   .stepLimit(IconConverter.toBigNumber(40000000))
//   .version(IconConverter.toBigNumber(3))
//   .method(method)
//   .params(params)
//   .build();

export const getSettingPriceStatus = () => {
  return new Promise(async (resolve, reject) => {
    try {
      const call = callBuilder
        .to(MARKETPLACE_SCORE)
        .method("isSettingEnabled")
        .params({
          score: GANGSTA_SCORE,
        })
        .build();
      const response = await iconService.call(call).execute();
      resolve(response);
    } catch (e) {
      resolve({
        error: e,
        msg: "Invalid",
      });
    }
  });
};

export const getBuyingPriceStatus = () => {
  return new Promise(async (resolve, reject) => {
    try {
      const call = callBuilder
        .to(MARKETPLACE_SCORE)
        .method("isBuyingEnabled")
        .params({
          score: GANGSTA_SCORE,
        })
        .build();
      const response = await iconService.call(call).execute();
      resolve(response);
    } catch (e) {
      resolve({
        error: e,
        msg: "Invalid",
      });
    }
  });
};

// export const removeFromListing = (nft_id) => {
//   return new Promise(async (resolve, reject) => {
//     try {
//       const call = callBuilder
//         .to(MARKETPLACE_SCORE)
//         .method("remove_from_sale")
//         .params({
//           nft_id: `0x${parseInt(nft_id).toString(16)}`,
//         })
//         .build();
//       const response = await iconService.call(call).execute();
//       resolve(response);
//     } catch (e) {
//       resolve({
//         msg: "Invalid",
//         error: e,
//       });
//     }
//   });
// };

export const removeFromListing = async (
  payload,
  walletType,
  walletPath = null,
  transport = null
) => {
  try {
    const sellDetails = await getSaleByNftId(payload.nft_id);
    const saleId = sellDetails.id;
    const callTransactionBuilder = new IconBuilder.CallTransactionBuilder();

    const params = {
      saleId: IconConverter.toHex(saleId),
    };
    const stepLimit = await getStepLimit(
      payload.address,
      "removeFromSale",
      MARKETPLACE_SCORE,
      params,
      0
    );
    const txObj = callTransactionBuilder
      .from(payload.address)
      .to(MARKETPLACE_SCORE)
      .value(IconAmount.of(0, IconAmount.Unit.ICX).toLoop())
      .stepLimit(stepLimit)
      .nid(IconConverter.toBigNumber(NETWORK_ID))
      .nonce(IconConverter.toBigNumber("1"))
      .version(IconConverter.toBigNumber("3"))
      .timestamp(new Date().getTime() * 1000)
      .method("removeFromSale")
      .params(params)
      .build();

    if (walletType == WALLET_TYPE.LEDGER) {
      const signedTransaction = await signTransaction(
        txObj,
        walletPath,
        transport
      );
      const res = await iconService
        .sendTransaction(signedTransaction)
        .execute();

      return res;
    } else {
      const scoreData = {};

      scoreData.value = JSON.stringify({
        jsonrpc: "2.0",
        method: "icx_sendTransaction",
        params: IconConverter.toRawTransaction(txObj),
        id: 50889,
      });

      const parsed = JSON.parse(scoreData.value);

      window.dispatchEvent(
        new CustomEvent("ICONEX_RELAY_REQUEST", {
          detail: {
            type: "REQUEST_JSON-RPC",
            payload: parsed,
          },
        })
      );
    }
  } catch (e) {
    console.log(e);
    return -1;
  }
};

export const get_batch_claim_limit = () => {
  return new Promise(async (resolve, reject) => {
    try {
      const call = callBuilder
        .to(SKILL_SCORE)
        .method("getBatchClaimLimit")
        .params({})
        .build();
      const response = await iconService.call(call).execute();
      resolve(response);
    } catch (e) {
      resolve({
        error: e,
        msg: "Invalid",
      });
    }
  });
};

export const claim_multiple_gbet = async (
  payload,
  walletType,
  walletPath = null,
  transport = null
) => {
  try {
    const callTransactionBuilder = new IconBuilder.CallTransactionBuilder();
    const params = {
      nftList: payload.nft_list,
    };
    const stepLimit = await getStepLimit(
      payload.address,
      "claimMultipleGbet",
      SKILL_SCORE,
      params,
      0
    );
    const txObj = callTransactionBuilder
      .from(payload.address)
      .to(SKILL_SCORE)
      .value(IconAmount.of(0, IconAmount.Unit.ICX).toLoop())
      .stepLimit(stepLimit)
      .nid(IconConverter.toBigNumber(NETWORK_ID))
      .nonce(IconConverter.toBigNumber("1"))
      .version(IconConverter.toBigNumber("3"))
      .timestamp(new Date().getTime() * 1000)
      .method("claimMultipleGbet")
      .params(params)
      .build();

    if (walletType == WALLET_TYPE.LEDGER) {
      const signedTransaction = await signTransaction(
        txObj,
        walletPath,
        transport
      );
      const res = await iconService
        .sendTransaction(signedTransaction)
        .execute();
      console.log(res);
      return res;
    } else {
      const scoreData = {};

      scoreData.value = JSON.stringify({
        jsonrpc: "2.0",
        method: "icx_sendTransaction",
        params: IconConverter.toRawTransaction(txObj),
        id: 50889,
      });

      const parsed = JSON.parse(scoreData.value);

      window.dispatchEvent(
        new CustomEvent("ICONEX_RELAY_REQUEST", {
          detail: {
            type: "REQUEST_JSON-RPC",
            payload: parsed,
          },
        })
      );
    }
  } catch (e) {
    console.log(e);
    return -1;
  }
};

function StringToHex(str) {
  var hex, i;

  var result = "";
  for (i = 0; i < str.length; i++) {
    hex = str.charCodeAt(i).toString(16);
    result += ("" + hex).slice(-4);
  }

  return result;
}

export const getBalanceOf = (address) => {
  return new Promise(async (resolve, reject) => {
    try {
      const call = callBuilder
        .to(VESTING_SCORE)
        .method("balanceOf")
        .params({ owner: address })
        .build();
      const response = await iconService.call(call).execute();
      resolve(response);
    } catch (e) {
      resolve({
        error: e,
        msg: "Invalid",
      });
    }
  });
};

export const getBalanceOfOn = (address, reserveId) => {
  return new Promise(async (resolve, reject) => {
    try {
      const call = callBuilder
        .to(VESTING_SCORE)
        .method("balanceOfOn")
        .params({ owner: address, reserveId: reserveId })
        .build();
      const response = await iconService.call(call).execute();
      resolve(response);
    } catch (e) {
      resolve({
        error: e,
        msg: "Invalid",
      });
    }
  });
};

export const claimAmount = async (address, walletType) => {
  try {
    const callTransactionBuilder = new IconBuilder.CallTransactionBuilder();
    const stepLimit = await getStepLimit(
      address,
      "claim",
      VESTING_SCORE,
      {},
      0
    );
    const txObj = callTransactionBuilder
      .from(address)
      .to(VESTING_SCORE)
      .value(IconAmount.of(0, IconAmount.Unit.ICX).toLoop())
      .stepLimit(stepLimit)
      .nid(IconConverter.toBigNumber(NETWORK_ID))
      .nonce(IconConverter.toBigNumber("1"))
      .version(IconConverter.toBigNumber("3"))
      .timestamp(new Date().getTime() * 1000)
      .method("claim")
      .params({})
      .build();

    if (walletType == WALLET_TYPE.LEDGER) {
      const signedTransaction = await signTransaction(
        txObj,
        walletPath,
        transport
      );
      const res = await iconService
        .sendTransaction(signedTransaction)
        .execute();
      console.log(res);
      return res;
    } else {
      const scoreData = {};

      scoreData.value = JSON.stringify({
        jsonrpc: "2.0",
        method: "icx_sendTransaction",
        params: IconConverter.toRawTransaction(txObj),
        id: 50889,
      });

      const parsed = JSON.parse(scoreData.value);
      console.log(parsed);

      window.dispatchEvent(
        new CustomEvent("ICONEX_RELAY_REQUEST", {
          detail: {
            type: "REQUEST_JSON-RPC",
            payload: parsed,
          },
        })
      );
    }
  } catch (e) {
    console.log(e);
    return -1;
  }
};

export const create_story = async (
  payload,
  walletType,
  walletPath = null,
  transport = null
) => {
  try {
    const callTransactionBuilder = new IconBuilder.CallTransactionBuilder();
    console.log(payload);
    const params = {
      nftId: payload.nft_id,
      content: payload.content,
    };
    const stepLimit = await getStepLimit(
      payload.address,
      "appendStory",
      STORY_SCORE,
      params,
      0
    );
    const txObj = callTransactionBuilder
      .from(payload.address)
      .to(STORY_SCORE)
      .value(IconAmount.of(0, IconAmount.Unit.ICX).toLoop())
      .stepLimit(stepLimit)
      .nid(IconConverter.toBigNumber(NETWORK_ID))
      .nonce(IconConverter.toBigNumber("1"))
      .version(IconConverter.toBigNumber("3"))
      .timestamp(new Date().getTime() * 1000)
      .method("appendStory")
      .params(params)
      .build();

    if (walletType == WALLET_TYPE.LEDGER) {
      const signedTransaction = await signTransaction(
        txObj,
        walletPath,
        transport
      );
      const res = await iconService
        .sendTransaction(signedTransaction)
        .execute();
      console.log(res);
      return res;
    } else {
      const scoreData = {};

      scoreData.value = JSON.stringify({
        jsonrpc: "2.0",
        method: "icx_sendTransaction",
        params: IconConverter.toRawTransaction(txObj),
        id: 50889,
      });

      const parsed = JSON.parse(scoreData.value);

      window.dispatchEvent(
        new CustomEvent("ICONEX_RELAY_REQUEST", {
          detail: {
            type: "REQUEST_JSON-RPC",
            payload: parsed,
          },
        })
      );
    }
  } catch (e) {
    console.log(e);
    return -1;
  }
};

export const get_story_cost = (type) => {
  return new Promise(async (resolve, reject) => {
    try {
      const call = callBuilder
        .to(STORY_SCORE)
        .method("getStoryCost")
        .params({ type: type })
        .build();
      const response = await iconService.call(call).execute();
      resolve(response);
    } catch (e) {
      resolve({
        error: e,
        msg: "Invalid",
      });
    }
  });
};

export const get_story = (nft_id, page) => {
  return new Promise(async (resolve, reject) => {
    try {
      const call = callBuilder
        .to(STORY_SCORE)
        .method("getStory")
        .params({ page: page.toString(), nftId: nft_id, includeDeleted: "0x0" })
        .build();
      const response = await iconService.call(call).execute();
      resolve(response);
    } catch (e) {
      resolve({
        error: e,
        msg: "Invalid",
      });
    }
  });
};

export const get_story_page_count = (nft_id) => {
  return new Promise(async (resolve, reject) => {
    try {
      const call = callBuilder
        .to(STORY_SCORE)
        .method("getStoryPageCount")
        .params({ nftId: nft_id, includeDeleted: "0x0" })
        .build();
      const response = await iconService.call(call).execute();
      resolve(response);
    } catch (e) {
      resolve({
        error: e,
        msg: "Invalid",
      });
    }
  });
};

export const get_story_per_page = () => {
  return new Promise(async (resolve, reject) => {
    try {
      const call = callBuilder
        .to(STORY_SCORE)
        .method("getStoryPerPage")
        .params({})
        .build();
      const response = await iconService.call(call).execute();
      resolve(response);
    } catch (e) {
      resolve({
        error: e,
        msg: "Invalid",
      });
    }
  });
};

export const delete_story = async (
  payload,
  walletType,
  walletPath = null,
  transport = null
) => {
  try {
    const callTransactionBuilder = new IconBuilder.CallTransactionBuilder();
    const params = {
      nftId: "0x" + parseInt(payload.nft_id).toString(16),
      storyId: "0x" + parseInt(payload.story_id).toString(16),
    };
    const stepLimit = await getStepLimit(
      payload.address,
      "deleteStory",
      STORY_SCORE,
      params,
      0
    );
    const txObj = callTransactionBuilder
      .from(payload.address)
      .to(STORY_SCORE)
      .value(IconAmount.of(0, IconAmount.Unit.ICX).toLoop())
      .stepLimit(stepLimit)
      .nid(IconConverter.toBigNumber(NETWORK_ID))
      .nonce(IconConverter.toBigNumber("1"))
      .version(IconConverter.toBigNumber("3"))
      .timestamp(new Date().getTime() * 1000)
      .method("deleteStory")
      .params(params)
      .build();

    if (walletType == WALLET_TYPE.LEDGER) {
      const signedTransaction = await signTransaction(
        txObj,
        walletPath,
        transport
      );
      const res = await iconService
        .sendTransaction(signedTransaction)
        .execute();
      console.log(res);
      return res;
    } else {
      const scoreData = {};

      scoreData.value = JSON.stringify({
        jsonrpc: "2.0",
        method: "icx_sendTransaction",
        params: IconConverter.toRawTransaction(txObj),
        id: 50889,
      });

      const parsed = JSON.parse(scoreData.value);

      window.dispatchEvent(
        new CustomEvent("ICONEX_RELAY_REQUEST", {
          detail: {
            type: "REQUEST_JSON-RPC",
            payload: parsed,
          },
        })
      );
    }
  } catch (e) {
    console.log(e);
    return -1;
  }
};

export const getGGBalanceOf = (hx) => {
  return new Promise(async (resolve, reject) => {
    try {
      const call = callBuilder
        .to(GG_SCORE)
        .method("balanceOf")
        .params({
          _owner: hx,
        })
        .build();
      // console.log(call);
      const response = await iconService.call(call).execute();
      resolve(response);
    } catch (e) {
      resolve({
        error: e,
        msg: "Invalid",
      });
    }
  });
};

export const get_tx_fee_rate = () => {
  return new Promise(async (resolve, reject) => {
    try {
      const call = callBuilder
        .to(MARKETPLACE_SCORE)
        .method("getMarketplaceFee")
        .params({ scoreAddress: GANGSTA_SCORE })
        .build();
      const response = await iconService.call(call).execute();
      resolve(response);
    } catch (e) {
      ({
        error: e,
        msg: "Invalid",
      });
    }
  });
};

export const getGoldenKeyCount = (hx) => {
  return new Promise(async (resolve, reject) => {
    try {
      const call = callBuilder
        .to(GOLDEN_KEY_SCORE)
        .method("balanceOf")
        .params({
          _owner: hx,
          _id: "1",
        })
        .build();
      const response = await iconService.call(call).execute();
      resolve(parseInt(response, 16));
    } catch (e) {
      console.log({
        error: e,
        msg: "Invalid",
      });
    }
  });
};

export const transferNFT = async (
  { fromAddress, toAddress, nft_id },
  walletType,
  walletPath = null,
  transport = null
) => {
  try {
    const callTransactionBuilder = new IconBuilder.CallTransactionBuilder();
    const params = {
      _from: fromAddress,
      _to: toAddress,
      _id: nft_id.toString(),
      _value: "1",
    };
    const stepLimit = await getStepLimit(
      fromAddress,
      "transferFrom",
      GANGSTA_SCORE,
      params,
      0
    );
    const txObj = callTransactionBuilder
      .from(fromAddress)
      .to(GANGSTA_SCORE)
      .value(IconAmount.of(0, IconAmount.Unit.ICX).toLoop())
      .stepLimit(stepLimit)
      .nid(IconConverter.toBigNumber(NETWORK_ID))
      .nonce(IconConverter.toBigNumber("1"))
      .version(IconConverter.toBigNumber("3"))
      .timestamp(new Date().getTime() * 1000)
      .method("transferFrom")
      .params(params)
      .build();

    if (walletType == WALLET_TYPE.LEDGER) {
      const signedTransaction = await signTransaction(
        txObj,
        walletPath,
        transport
      );

      const res = await iconService
        .sendTransaction(signedTransaction)
        .execute();

      return res;
    } else {
      const scoreData = {};

      scoreData.value = JSON.stringify({
        jsonrpc: "2.0",
        method: "icx_sendTransaction",
        params: IconConverter.toRawTransaction(txObj),
        id: 50889,
      });

      const parsed = JSON.parse(scoreData.value);

      window.dispatchEvent(
        new CustomEvent("ICONEX_RELAY_REQUEST", {
          detail: {
            type: "REQUEST_JSON-RPC",
            payload: parsed,
          },
        })
      );
    }
  } catch (e) {
    return -1;
  }
};

export const getReservations = (address) => {
  return new Promise(async (resolve, reject) => {
    try {
      const call = callBuilder
        .to(ILLUSTRATION_SCORE)
        .method("getReservations")
        .params({
          address: address,
        })
        .build();
      const response = await iconService.call(call).execute();
      resolve(response);
    } catch (e) {
      console.log({
        error: e,
        msg: "Invalid",
      });
    }
  });
};

export const getAllReservations = () => {
  return new Promise(async (resolve, reject) => {
    try {
      const call = callBuilder
        .to(ILLUSTRATION_SCORE)
        .method("getReservations")
        .params({})
        .build();
      const response = await iconService.call(call).execute();
      resolve(response);
    } catch (e) {
      console.log({
        error: e,
        msg: "Invalid",
      });
    }
  });
};

export const getIllustrationTokenURI = (nft_id) => {
  return new Promise(async (resolve, reject) => {
    try {
      const call = callBuilder
        .to(ILLUSTRATION_SCORE)
        .method("tokenURI")
        .params({
          _id: nft_id,
        })
        .build();
      const response = await iconService.call(call).execute();
      resolve(response);
    } catch (e) {
      console.log({
        error: e,
        msg: "Invalid",
      });
    }
  });
};

export const ownerOf = (nft_id) => {
  return new Promise(async (resolve, reject) => {
    try {
      const call = callBuilder
        .to(GANGSTA_SCORE)
        .method("ownerOf")
        .params({
          _tokenId: nft_id,
        })
        .build();
      const response = await iconService.call(call).execute();
      resolve(response);
    } catch (e) {
      console.log({
        error: e,
        msg: "Invalid",
      });
    }
  });
};

export const ownerOfIllustration = (nft_id) => {
  return new Promise(async (resolve, reject) => {
    try {
      const call = callBuilder
        .to(ILLUSTRATION_SCORE)
        .method("ownerOf")
        .params({
          id: nft_id,
        })
        .build();
      const response = await iconService.call(call).execute();
      resolve(response);
    } catch (e) {
      console.log({
        error: e,
        msg: "Invalid",
      });
    }
  });
};

export const migrateNFTs = async (
  payload,
  walletType,
  walletPath = null,
  transport = null
) => {
  return new Promise(async (resolve, reject) => {
    try {
      const { fromAddress, toAddress } = payload;
      const callTransactionBuilder = new IconBuilder.CallTransactionBuilder();
      const params = {
        suiAddress: toAddress
      };
      const stepLimit = await getStepLimit(
        fromAddress,
        "burnAllMyNFTs",
        GANGSTA_SCORE,
        params,
        0
      );
      const txObj = callTransactionBuilder
        .from(fromAddress)
        .to(GANGSTA_SCORE)
        .value(IconAmount.of(0, IconAmount.Unit.ICX).toLoop())
        .stepLimit(stepLimit)
        .nid(IconConverter.toBigNumber(NETWORK_ID))
        .nonce(IconConverter.toBigNumber("1"))
        .version(IconConverter.toBigNumber("3"))
        .timestamp(new Date().getTime() * 1000)
        .method("burnAllMyNFTs")
        .params(params)
        .build();


        if (walletType == WALLET_TYPE.LEDGER) {
          const signedTransaction = await signTransaction(
            txObj,
            walletPath,
            transport
          );
    
          const res = await iconService
            .sendTransaction(signedTransaction)
            .execute();
    
          return res;
        } else {
          const scoreData = {};
    
          scoreData.value = JSON.stringify({
            jsonrpc: "2.0",
            method: "icx_sendTransaction",
            params: IconConverter.toRawTransaction(txObj),
            id: 50889,
          });
    
          const parsed = JSON.parse(scoreData.value);
    
          window.dispatchEvent(
            new CustomEvent("ICONEX_RELAY_REQUEST", {
              detail: {
                type: "REQUEST_JSON-RPC",
                payload: parsed,
              },
            })
          );
        }
    } catch (error) {
      console.error("Migration error:", error);
      reject(error);
    }
  });
};
